import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { AbstractComponent } from '../../components/abstract-component/abstract.component';
import { ListLoader } from '../../components/list-loader/list-loader';
import { EigentumanteilMasterDetailVM } from '../../../core/services/eigentum-viewmodel.service';
import { FieldMetaData } from '../../../core/av-api';
import { NavTarget } from '../../components/link/link.component';
import { LoadingState } from '../../components/list-loader/loading-state';
import { ComparatorService } from '../../services/comparator-service';
import { ListLoaderInfinite } from '../../components/list-loader/list-loader-infinite';
import { ListLoaderWrapperComponent } from '../../components/list-loader-wrapper/list-loader-wrapper.component';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { HistoryDirective } from '../../directives/history.directive';
import { KompaktGrundstueckComponent } from '../../kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { RechtsgrundComponent } from '../../kompaktdarstellung/rechtsgrund/rechtsgrund.component';
import { ReadMoreComponent } from '../read-more/read-more.component';
import { KompaktPersongbComponent } from '../../kompaktdarstellung/persongb/kompakt-persongb.component';
import { KompaktPersonenadresseComponent } from '../../kompaktdarstellung/personenadresse/kompakt-personenadresse.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslateNamePipe } from '../../pipes/simple/translate-name.pipe';
import { UniqueErwerbsgrundPipe } from '../../pipes/simple/unique-erwerbsgrund.pipe';
import { AppPermissionService } from '../../../core/services/app-permission.service';
import { PermissionResponse } from '../../../core/base-api';
import { PersonGbListEntryMeta, PersonGbMasterDetailEntryMeta } from '../../../core/gb-api';
import { KompaktTimepointComponent } from '../../kompaktdarstellung/timepoint/kompakt-timepoint.component';
import PermissionsEnum = PermissionResponse.PermissionsEnum;
import AuskunftsperreEnum = PersonGbListEntryMeta.AuskunftsperreEnum;

@Component({
  selector: 'cap-eigentum-table',
  templateUrl: './eigentum-table.component.html',
  styleUrls: ['./eigentum-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    NgClass,
    HistoryDirective,
    NgStyle,
    KompaktGrundstueckComponent,
    RechtsgrundComponent,
    ReadMoreComponent,
    KompaktPersongbComponent,
    KompaktPersonenadresseComponent,
    TranslatePipe,
    TranslateNamePipe,
    UniqueErwerbsgrundPipe,
    KompaktTimepointComponent
  ]
})
export class EigentumTableComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input()
  showAdressen: boolean = true;
  @Input()
  hasRight: boolean = true;
  @Input()
  showHistorischeDaten: boolean = true;
  @Input()
  navigateToGrundstueck: NavTarget = NavTarget.NAV_GRUNDSTUECK;
  @Input()
  showEigentumHeaderWithText: boolean = true;
  hasParzellenindex: boolean;
  showBelegColumn: boolean;
  private _listLoader: ListLoader<EigentumanteilMasterDetailVM>;
  private changeDetectionTimeout: any;
  protected readonly PersonGbListEntryMeta = PersonGbListEntryMeta;
  protected readonly AuskunftsperreEnum = AuskunftsperreEnum;
  protected readonly PersonGbMasterDetailEntryMeta = PersonGbMasterDetailEntryMeta;

  constructor(
    protected router: Router,
    protected appformatConfigService: FormatConfigService,
    private cdr: ChangeDetectorRef,
    private comparatorService: ComparatorService,
    private appPersmissionService: AppPermissionService
  ) {
    super();
    keysToTranslate<string>(['KeineDatenVorhanden', 'grundstueck.ListTitel', 'PlanZuGrundstueckAnzeigen', 'Kurzbeschreibung']);
  }

  ngOnInit(): void {
    this.activateChangeDetectionTrigger();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    clearTimeout(this.changeDetectionTimeout);
  }

  @Input()
  set listLoader(listLoader: ListLoader<EigentumanteilMasterDetailVM>) {
    this._listLoader = listLoader;
    if (listLoader) {
      this.addSub(
        listLoader.items$.subscribe((items) => {
          if (items.length > 0) {
            this.hasParzellenindex = items[0].object.versions[0].mParzellenindex.access !== FieldMetaData.AccessEnum.DEACTIVATED;
            this.showBelegColumn = items[0].showBelegColumn;
            this.addSub(
              listLoader.state$.subscribe((state) => {
                if (state == LoadingState.ALL_DATA_LOADED) {
                  const loader = listLoader as ListLoaderInfinite<EigentumanteilMasterDetailVM>;
                  loader.allItems.sort((a, b) => this.comparatorService.eigentumanteilMasterComparator(a.object, b.object));
                }
              })
            );
          }
        })
      );
    }
  }

  get listLoader(): ListLoader<EigentumanteilMasterDetailVM> {
    return this._listLoader;
  }

  hasAuskunftsperre(personGbHasAuskunftssperre: PersonGbMasterDetailEntryMeta.HasAuskunftssperreEnum): AuskunftsperreEnum {
    return personGbHasAuskunftssperre === PersonGbMasterDetailEntryMeta.HasAuskunftssperreEnum.AUSKUNFT_SPERRE
      ? AuskunftsperreEnum.AUSKUNFT_SPERRE
      : AuskunftsperreEnum.KEINE;
  }

  hasAdresssperre(isAdresseGesperrt: boolean): AuskunftsperreEnum {
    return isAdresseGesperrt && this.appPersmissionService.hasPermission(PermissionsEnum.ADRESSSPERRE)
      ? AuskunftsperreEnum.ADRESS_SPERRE
      : AuskunftsperreEnum.KEINE;
  }

  private activateChangeDetectionTrigger() {
    this.changeDetectionTimeout = setTimeout(() => {
      this.cdr.markForCheck();
      this.activateChangeDetectionTrigger();
    }, 3000);
  }
}
